import { Icon, Loader } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { PropsWithChildren, ReactNode } from 'react'
import { DetectBlur } from '@/containers/util-containers'
import { cn } from '@/lib/utils'
import { useOverflowScrollIndicator } from '@/utils'
import { useHotkey } from '@/utils/hooks'
import { Button } from './ui'

interface PromptModalProps extends PropsWithChildren {
  title: string
  /** Applied to the modal box — use for width, e.g. `w-[600px]`. */
  className?: string

  cancelText?: string
  submitText?: ReactNode
  cancelDisabled?: boolean
  submitDisabled?: boolean
  loading?: boolean

  cancelOnClickOutside?: boolean
  cancelOnEscape?: boolean

  /** If defined, shows a close icon in the top right corner. */
  onClose?: () => void
  onCancel?: () => void
  onSubmit?: () => void
}

/**
 * Modal shell for the imperative `usePrompt` system. The body scrolls on
 * overflow while the header and footer stay pinned, so the action buttons are
 * always visible regardless of content height.
 */
export function PromptModal({
  children,
  className,

  title,
  cancelText,
  submitText,
  cancelDisabled = false,
  submitDisabled = false,
  loading = false,

  cancelOnClickOutside = false,
  cancelOnEscape = false,

  onClose,
  onCancel,
  onSubmit,
}: PromptModalProps) {
  const translations = useTranslate({
    cancel: 'common.cancel',
    ok: 'common.ok',
  })

  const { ref, showTopShadow, showBottomShadow } =
    useOverflowScrollIndicator<HTMLDivElement>()

  const hasCancel = typeof onCancel !== 'undefined'
  const hasSubmit = typeof onSubmit !== 'undefined'
  const hasFooter = hasCancel || hasSubmit

  useHotkey('escape', () => onCancel?.(), { disabled: !cancelOnEscape })

  return (
    <DetectBlur
      active={cancelOnClickOutside}
      onClickOutside={() => onCancel?.()}
    >
      <div
        className={cn(
          'flex max-h-[92vh] w-[480px] max-w-[96vw] flex-col overflow-hidden rounded-lg bg-white',
          className
        )}
      >
        <header className="relative flex shrink-0 items-center p-4">
          <h2 className="m-0 flex items-center gap-2 text-xl font-semibold">
            {title}
            {loading && <Loader.Spinner size={20} thickness={2} />}
          </h2>

          {typeof onClose !== 'undefined' && (
            <Icon
              className="absolute top-4 right-4"
              icon="times"
              type="light"
              size="1.2rem"
              color="gray6"
              hoverColor="secondary"
              cursor="pointer"
              onClick={onClose}
            />
          )}
        </header>

        <div className="relative">
          <div
            ref={ref}
            className="max-h-[60vh] overflow-y-auto px-4 pb-4 sm:max-h-[70vh]"
          >
            {children}
          </div>

          <div
            className={cn(
              'pointer-events-none absolute inset-x-0 top-0 h-6 bg-linear-to-b from-white to-transparent transition-opacity',
              !showTopShadow && 'opacity-0'
            )}
          />
          <div
            className={cn(
              'pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-linear-to-t from-white to-transparent transition-opacity',
              !showBottomShadow && 'opacity-0'
            )}
          />
        </div>

        {hasFooter && (
          <footer className="flex shrink-0 justify-end gap-3 border-t border-border p-4">
            {hasCancel && (
              <Button
                variant="outline"
                size="lg"
                disabled={cancelDisabled}
                onClick={onCancel}
              >
                {cancelText ?? translations.cancel}
              </Button>
            )}

            {hasSubmit && (
              <Button size="lg" disabled={submitDisabled} onClick={onSubmit}>
                {submitText ?? translations.ok}
              </Button>
            )}
          </footer>
        )}
      </div>
    </DetectBlur>
  )
}
